import { useCallback, useEffect, useState } from "react";
import { apiFetch, useApp } from "../../context/AppContext";
import { Button } from "./UI";
import CollapsibleSettingsCard from "./CollapsibleSettingsCard";
import { Bell, Volume2, VolumeX } from "lucide-react";

const CATEGORY_LABELS = {
  dsar: {
    title: "DSAR Records",
    desc: "Alerts when new DSAR requests are submitted or completed.",
  },
  consent_variance: {
    title: "Consent Variance",
    desc: "Alerts when consent mismatches are detected or reinitiation tasks fail.",
  },
  breach: {
    title: "Breach Incidents",
    desc: "Alerts when new data breaches are reported or escalated.",
  },
  stats: {
    title: "Widget Stats Data",
    desc: "Alerts when external telemetry or widget stats are pushed.",
  },
  form_questions: {
    title: "Form Questions",
    desc: "Alerts when questions are created, updated, or deleted in Form Question Management.",
  },
  assessment: {
    title: "Gap Assessment",
    desc: "Alerts when assessment cycles are created, opened, closed, or when an assessment is unlocked.",
  },
};

/** Notification category → license module(s); omit = always visible. */
const CATEGORY_MODULE = {
  dsar: "dsar",
  consent_variance: "consent",
  breach: "breach",
  stats: ["consent", "dsar", "dspm"],
  form_questions: ["gap", "ropa", "dpia", "tprm"],
  assessment: "gap",
};

function categoryModuleAllowed(category, allowedModules) {
  const required = CATEGORY_MODULE[category];
  if (!required) return true;
  const keys = Array.isArray(required) ? required : [required];
  return keys.some((key) => allowedModules.includes(key));
}

export default function NotificationSettingsPanel() {
  const { currentUser, showNotification, notificationSoundEnabled, setNotificationSoundEnabled } = useApp();
  const allowedModules = currentUser?.modules || [];
  const visibleCategories = Object.entries(CATEGORY_LABELS).filter(([cat]) =>
    categoryModuleAllowed(cat, allowedModules)
  );
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch("/notifications/settings");
      if (!res.ok) throw new Error("Failed to load notification settings");
      const data = await res.json();
      
      // Map to category dictionary
      const settingsMap = {};
      data.forEach((item) => {
        settingsMap[item.category] = {
          send_to_admins: item.send_to_admins,
          send_to_users: item.send_to_users,
        };
      });
      setSettings(settingsMap);
    } catch (e) {
      showNotification(e.message || "Failed to load notification settings", "error");
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleToggle = (category, target) => {
    setSettings((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        [target]: !prev[category]?.[target],
      },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await apiFetch("/notifications/settings", {
        method: "PUT",
        body: JSON.stringify(settings),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Save failed");
      }
      showNotification("Notification settings saved successfully", "success");
    } catch (e) {
      showNotification(e.message || "Failed to save settings", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <CollapsibleSettingsCard
        tourId="tour-tenant-settings-notifications"
        title="Notification Routing Preferences"
        icon={<Bell size={18} className="text-white" />}
        iconClassName="bg-gradient-to-br from-blue-500 to-indigo-600"
      >
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-slate-200 rounded" />
          <div className="h-4 bg-slate-200 rounded w-5/6" />
          <div className="h-4 bg-slate-200 rounded w-2/3" />
        </div>
      </CollapsibleSettingsCard>
    );
  }

  if (!visibleCategories.length) {
    return null;
  }

  return (
    <CollapsibleSettingsCard
      tourId="tour-tenant-settings-notifications"
      title="Notification Routing Preferences"
      description="Configure which modules trigger notifications for Tenant Admins vs standard Users."
      icon={<Bell size={18} className="text-white" />}
      iconClassName="bg-gradient-to-br from-blue-500 to-indigo-600"
    >
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50/70 px-4 py-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-900 flex items-center gap-2">
              {notificationSoundEnabled ? (
                <Volume2 size={15} className="text-indigo-600 shrink-0" />
              ) : (
                <VolumeX size={15} className="text-slate-400 shrink-0" />
              )}
              Notification sound
            </p>
            <p className="text-xs text-slate-500 mt-0.5">
              Play one sound when a new inbox notification arrives. This is only for your browser.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setNotificationSoundEnabled(!notificationSoundEnabled)}
            className={`inline-flex items-center justify-center w-10 h-6 rounded-full transition-colors relative focus:outline-none shrink-0 ${
              notificationSoundEnabled ? "bg-indigo-600" : "bg-slate-200"
            }`}
            aria-pressed={notificationSoundEnabled}
            aria-label={notificationSoundEnabled ? "Turn notification sound off" : "Turn notification sound on"}
          >
            <span
              className={`w-4 h-4 rounded-full bg-white transition-transform absolute shadow-sm ${
                notificationSoundEnabled ? "translate-x-2" : "-translate-x-2"
              }`}
            />
          </button>
        </div>
        <div className="overflow-x-auto border border-slate-100 rounded-xl">
          <table className="min-w-full divide-y divide-slate-100 text-left">
            <thead className="bg-slate-50/75 text-xs text-slate-500 uppercase tracking-wider font-semibold">
              <tr>
                <th className="py-3 px-4">Notification Type</th>
                <th className="py-3 px-4 text-center w-36">Send to Admins</th>
                <th className="py-3 px-4 text-center w-36">Send to Users</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm text-slate-700">
              {visibleCategories.map(([cat, info]) => {
                const adminChecked = settings[cat]?.send_to_admins ?? true;
                const userChecked = settings[cat]?.send_to_users ?? true;
                return (
                  <tr key={cat} className="hover:bg-slate-50/50 transition-colors">
                    <td className="py-4 px-4 max-w-sm">
                      <div className="font-medium text-slate-900">{info.title}</div>
                      <div className="text-xs text-slate-400 mt-0.5">{info.desc}</div>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <button
                        onClick={() => handleToggle(cat, "send_to_admins")}
                        className={`inline-flex items-center justify-center w-10 h-6 rounded-full transition-colors relative focus:outline-none ${
                          adminChecked ? "bg-indigo-600" : "bg-slate-200"
                        }`}
                      >
                        <span
                          className={`w-4 h-4 rounded-full bg-white transition-transform absolute shadow-sm ${
                            adminChecked ? "translate-x-2" : "-translate-x-2"
                          }`}
                        />
                      </button>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <button
                        onClick={() => handleToggle(cat, "send_to_users")}
                        className={`inline-flex items-center justify-center w-10 h-6 rounded-full transition-colors relative focus:outline-none ${
                          userChecked ? "bg-indigo-600" : "bg-slate-200"
                        }`}
                      >
                        <span
                          className={`w-4 h-4 rounded-full bg-white transition-transform absolute shadow-sm ${
                            userChecked ? "translate-x-2" : "-translate-x-2"
                          }`}
                        />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end pt-2">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm"
          >
            {saving ? "Saving..." : "Save Settings"}
          </Button>
        </div>
      </div>
    </CollapsibleSettingsCard>
  );
}
